import React from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Phone, MapPin, Briefcase, Users, IndianRupee, Calendar, Clock, Send, CreditCard } from 'lucide-react';
import EmployeeIDCardModal from './EmployeeIDCardModal';
import { useState } from 'react';

export default function ViewEmployeeModal({ isOpen, onClose, employee }) {
  const [isIDCardOpen, setIsIDCardOpen] = useState(false);
  if (!isOpen || !employee) return null;

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[600px] flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Green Header Banner */}
        <div className="relative bg-[#0f7b63] px-6 pt-5 pb-12 flex flex-col items-center text-center">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
          >
            <X size={20} />
          </button>
          <h2 className="text-xl font-black text-white tracking-tight">{employee.name}</h2>
          <p className="text-[12px] font-semibold text-emerald-200 mt-0.5 uppercase tracking-wider">{employee.id}</p>
        </div>

        {/* Avatar overlapping banner */}
        <div className="relative flex justify-start px-6">
          <div className="-mt-8 w-16 h-16 rounded-full border-4 border-white shadow-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-slate-100 text-emerald-600 font-black text-xl uppercase">
            {employee.avatar ? (
              <img src={employee.avatar} alt={employee.name} className="w-full h-full object-cover" />
            ) : (
              employee.initials || employee.name?.charAt(0) || 'U'
            )}
          </div>
          {/* Action buttons right side */}
          <div className="absolute right-6 top-1.5 flex items-center gap-1.5">
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f7b63] text-white text-[11px] font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm">
              <Send size={12} /> Message
            </button>
            <button 
              onClick={() => setIsIDCardOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-[11px] font-bold rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
            >
              <CreditCard size={12} /> ID Card
            </button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="px-6 pt-3 pb-5 flex-1 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-2 gap-8">
            
            {/* Professional Details */}
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">PROFESSIONAL DETAILS</h3>
              <div className="space-y-4">
                
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                    <Briefcase size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Designation</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">{employee.role}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                    <Users size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Department</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">{employee.department}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                    <IndianRupee size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Basic Salary</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">₹ {employee.salary || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                    <Calendar size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Joining Date</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">{employee.joinDate}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                    <Clock size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Shift Schedule</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">{employee.shift}</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Personal Information */}
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">PERSONAL INFORMATION</h3>
              <div className="space-y-4">

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0 border border-slate-100">
                    <Mail size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Email Address</p>
                    <p className="text-[13px] font-bold text-blue-500 mt-0.5 break-all">{employee.user?.email || employee.email || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0 border border-slate-100">
                    <Phone size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Phone</p>
                    <p className="text-[14px] font-bold text-slate-800 mt-0.5">{employee.user?.phone || employee.phone || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0 border border-slate-100">
                    <MapPin size={16} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold">Address</p>
                    <p className="text-[13px] font-bold text-slate-700 mt-0.5 leading-relaxed">{employee.address || 'N/A'}</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <EmployeeIDCardModal 
        isOpen={isIDCardOpen}
        onClose={() => setIsIDCardOpen(false)}
        employee={employee}
      />
    </div>,
    document.body
  );
}
